import { useState } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, AlertTriangle, Trash2, Plus } from "lucide-react";

const HEADER_FIELDS = [
  ["egid", "N° EGID"], ["numero_dossier", "N° de dossier"], ["immeuble", "Immeuble"], ["npa", "NPA"], ["localite", "Localité"],
  ["etage", "Étage"], ["numero_objet", "N° d'objet"], ["nombre_pieces", "Nombre de pièces"], ["surface", "Surface (m²)"],
  ["proprietaire", "Propriétaire"], ["email_proprietaire", "E-mail du propriétaire"], ["dependances", "Dépendances"],
  ["locataire_entrant", "Locataire entrant"], ["email_locataire_entrant", "E-mail du locataire entrant"],
  ["locataire_sortant", "Locataire sortant"], ["email_locataire_sortant", "E-mail du locataire sortant"],
  ["nouvelle_adresse_sortant", "Nouvelle adresse du sortant"], ["telephone", "Téléphone"],
  ["gerant_responsable", "Gérant responsable"], ["date_constat", "Date du constat"], ["heure_constat", "Heure du constat"],
];
const MODULES = [
  ["deuxieme_salle_eau", "Deuxième salle d'eau"], ["wc_separe", "WC séparé"], ["balcon", "Balcon"], ["terrasse", "Terrasse"],
  ["jardin", "Jardin"], ["cave", "Cave"], ["galetas", "Galetas"], ["buanderie", "Buanderie privative"],
  ["place_parc", "Place de parc"], ["box", "Box"], ["reduit", "Réduit"],
];
const TYPES = [["entree", "Entrée"], ["sortie", "Sortie"], ["entree_sortie", "Entrée + sortie"]];
const IMPUT = [["inconnue", "Inconnue"], ["locataire", "Locataire"], ["bailleur", "Bailleur"], ["usure_normale", "Usure normale"]];
const fileUrl = (f) => `${API}${f.url.replace(/^\/api/, "")}`;

export function ImportReview({ initial, fichiers, typologies, onCreate }) {
  const [p, setPState] = useState(initial);
  const [creating, setCreating] = useState(false);
  const setP = (fn) => setPState((x) => fn(x));
  const setEnt = (k, v) => setP((x) => ({ ...x, entete: { ...(x.entete || {}), [k]: v } }));
  const setList = (key, i, k, v) => setP((x) => ({ ...x, [key]: x[key].map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));
  const delItem = (key, i) => setP((x) => ({ ...x, [key]: x[key].filter((_, j) => j !== i) }));
  const addItem = (key, item) => setP((x) => ({ ...x, [key]: [...(x[key] || []), item] }));
  const toggleModule = (m) => setP((x) => {
    const set = new Set(x.modules || []);
    set.has(m) ? set.delete(m) : set.add(m);
    return { ...x, modules: [...set] };
  });
  const togglePhoto = (i, idx) => setP((x) => ({
    ...x, observations: x.observations.map((o, j) => {
      if (j !== i) return o;
      const cur = new Set(o.photos_fichiers || []);
      cur.has(idx) ? cur.delete(idx) : cur.add(idx);
      return { ...o, photos_fichiers: [...cur] };
    }),
  }));
  const images = fichiers.map((f, idx) => ({ ...f, idx })).filter((f) => f.content_type.startsWith("image/"));
  const conf = Math.round((p.confiance || 0) * 100);
  const create = async () => { setCreating(true); try { await onCreate(p); } finally { setCreating(false); } };

  return (
    <div className="space-y-6" data-testid="import-review">
      <div className="bg-white rounded-xl border border-dd-light p-5 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold text-dd-night">Confiance de lecture : <span data-testid="import-confiance">{conf}%</span></p>
          <p className="text-xs text-dd-warm">Documents : {fichiers.map((f) => f.name).join(", ")}</p>
        </div>
        <Button onClick={create} disabled={creating} className="h-11 bg-dd-blue hover:bg-dd-night text-white" data-testid="import-create-btn">
          {creating ? <Loader2 className="animate-spin mr-2" size={18} /> : null} Créer le constat en brouillon
        </Button>
      </div>

      {p.avertissements?.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="import-warnings">
          <p className="flex items-center gap-2 font-semibold text-amber-900 text-sm"><AlertTriangle size={16} /> Points à vérifier</p>
          <ul className="list-disc ml-6 mt-2 text-sm text-amber-900 space-y-1">{p.avertissements.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}

      {images.length > 0 && (
        <Card title="Photos : original et version redressée">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="import-compare">
            {images.map((img) => (
              <div key={img.idx} className="rounded-lg border border-dd-light p-3" data-testid={`import-compare-${img.idx}`}>
                <p className="text-sm font-medium text-dd-night truncate mb-2">{img.name}</p>
                <div className="grid grid-cols-2 gap-2">
                  <figure>
                    <img src={img.original_url ? fileUrl({ url: img.original_url }) : fileUrl(img)} alt="" className="w-full h-56 object-contain bg-dd-light/40 rounded" />
                    <figcaption className="text-xs text-dd-warm mt-1 text-center">Original</figcaption>
                  </figure>
                  <figure>
                    <img src={fileUrl(img)} alt="" className="w-full h-56 object-contain bg-dd-light/40 rounded" />
                    <figcaption className="text-xs text-dd-warm mt-1 text-center">
                      Analysée{img.pretraitement?.recadre ? " · recadrée" : ""}{img.pretraitement?.rotation_deg ? ` · ${img.pretraitement.rotation_deg}°` : ""}
                    </figcaption>
                  </figure>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card title="Type & typologie">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-dd-night">Type de constat</Label>
            <Select value={p.type_constat || "sortie"} onValueChange={(v) => setP((x) => ({ ...x, type_constat: v }))}>
              <SelectTrigger className="mt-1 h-11" data-testid="import-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-dd-night">Typologie (génère les pièces du catalogue)</Label>
            <Select value={p.typologie_id || "none"} onValueChange={(v) => setP((x) => ({ ...x, typologie_id: v === "none" ? null : v }))}>
              <SelectTrigger className="mt-1 h-11" data-testid="import-typologie"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                {typologies.map((t) => <SelectItem key={t.id} value={t.id}>{t.libelle}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
          {MODULES.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-dd-night">
              <Checkbox checked={(p.modules || []).includes(k)} onCheckedChange={() => toggleModule(k)} data-testid={`import-module-${k}`} /> {l}
            </label>
          ))}
        </div>
      </Card>

      <Card title="Objet loué & parties">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {HEADER_FIELDS.map(([k, l]) => (
            <div key={k}>
              <Label className="text-sm text-dd-night">{l}</Label>
              <Input value={p.entete?.[k] || ""} onChange={(e) => setEnt(k, e.target.value)} className="mt-1 h-11" data-testid={`import-ent-${k}`}
                type={k === "date_constat" ? "date" : k === "heure_constat" ? "time" : "text"} />
            </div>
          ))}
        </div>
      </Card>

      <Card title="Clés & badges" action={<AddBtn onClick={() => addItem("cles", { designation: "", numero: "", nombre: 1, date_remise: "", date_restitution: "" })} testid="import-add-cle" />}>
        {(p.cles || []).map((c, i) => (
          <Row key={i} onDelete={() => delItem("cles", i)}>
            <Input placeholder="Désignation" value={c.designation || ""} onChange={(e) => setList("cles", i, "designation", e.target.value)} className="h-10" />
            <Input placeholder="N°" value={c.numero || ""} onChange={(e) => setList("cles", i, "numero", e.target.value)} className="h-10 w-28" />
            <Input placeholder="Nb" type="number" value={c.nombre ?? ""} onChange={(e) => setList("cles", i, "nombre", Number(e.target.value))} className="h-10 w-20" />
          </Row>
        ))}
        {!p.cles?.length && <Empty />}
      </Card>

      <Card title="Compteurs" action={<AddBtn onClick={() => addItem("compteurs", { type: "", numero: "", index_entree: "", index_sortie: "" })} testid="import-add-compteur" />}>
        {(p.compteurs || []).map((c, i) => (
          <Row key={i} onDelete={() => delItem("compteurs", i)}>
            <Input placeholder="Type" value={c.type || ""} onChange={(e) => setList("compteurs", i, "type", e.target.value)} className="h-10" />
            <Input placeholder="N°" value={c.numero || ""} onChange={(e) => setList("compteurs", i, "numero", e.target.value)} className="h-10 w-32" />
            <Input placeholder="Index entrée" value={c.index_entree || ""} onChange={(e) => setList("compteurs", i, "index_entree", e.target.value)} className="h-10 w-32" />
            <Input placeholder="Index sortie" value={c.index_sortie || ""} onChange={(e) => setList("compteurs", i, "index_sortie", e.target.value)} className="h-10 w-32" />
          </Row>
        ))}
        {!p.compteurs?.length && <Empty />}
      </Card>

      <Card title={`Observations (${p.observations?.length || 0})`} action={<AddBtn onClick={() => addItem("observations", { piece: "", element: "", description: "", nature: "defaut", imputation: "", montant: null, photos_fichiers: [] })} testid="import-add-obs" />}>
        <div className="space-y-3">
          {(p.observations || []).map((o, i) => (
            <div key={i} className={`rounded-lg border p-3 space-y-2 ${o.incertain ? "border-amber-300 bg-amber-50/40" : "border-dd-light"}`} data-testid={`import-obs-${i}`}>
              <div className="flex flex-wrap gap-2">
                <Input placeholder="Pièce" value={o.piece || ""} onChange={(e) => setList("observations", i, "piece", e.target.value)} className="h-10 w-40" />
                <Input placeholder="Élément" value={o.element || ""} onChange={(e) => setList("observations", i, "element", e.target.value)} className="h-10 flex-1 min-w-[160px]" />
                <Select value={o.imputation || "inconnue"} onValueChange={(v) => setList("observations", i, "imputation", v === "inconnue" ? "" : v)}>
                  <SelectTrigger className="h-10 w-40"><SelectValue placeholder="Imputation" /></SelectTrigger>
                  <SelectContent>{IMPUT.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="CHF" type="number" value={o.montant ?? ""} onChange={(e) => setList("observations", i, "montant", e.target.value === "" ? null : Number(e.target.value))} className="h-10 w-28" />
                <button onClick={() => delItem("observations", i)} className="text-dd-warm hover:text-destructive px-1" data-testid={`import-obs-del-${i}`}><Trash2 size={16} /></button>
              </div>
              <Textarea value={o.description || ""} onChange={(e) => setList("observations", i, "description", e.target.value)} rows={2} placeholder="Description du défaut / remarque" />
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center" data-testid={`import-obs-photos-${i}`}>
                  <span className="text-xs text-dd-warm">Photos :</span>
                  {images.map((img) => {
                    const on = (o.photos_fichiers || []).includes(img.idx);
                    return (
                      <button key={img.idx} type="button" onClick={() => togglePhoto(i, img.idx)} title={img.name}
                        className={`relative h-16 w-16 rounded-md overflow-hidden border-2 ${on ? "border-dd-blue" : "border-transparent opacity-50 hover:opacity-90"}`}
                        data-testid={`import-obs-${i}-photo-${img.idx}`}>
                        <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              )}
              {o.incertain && <p className="text-xs text-amber-800 flex items-center gap-1"><AlertTriangle size={12} /> Lecture incertaine — à vérifier</p>}
            </div>
          ))}
          {!p.observations?.length && <Empty />}
        </div>
      </Card>

      <Card title="Remarques générales">
        <Textarea value={p.remarques_generales || ""} onChange={(e) => setP((x) => ({ ...x, remarques_generales: e.target.value }))} rows={3} data-testid="import-remarques" />
      </Card>

      <div className="flex justify-end">
        <Button onClick={create} disabled={creating} className="h-12 bg-dd-blue hover:bg-dd-night text-white" data-testid="import-create-btn-bottom">
          {creating ? <Loader2 className="animate-spin mr-2" size={18} /> : null} Créer le constat en brouillon
        </Button>
      </div>
    </div>
  );
}

function Card({ title, children, action }) {
  return (
    <section className="bg-white rounded-xl border border-dd-light p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base md:text-lg font-semibold text-dd-night">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
function AddBtn({ onClick, testid }) {
  return <Button variant="outline" size="sm" onClick={onClick} className="h-9" data-testid={testid}><Plus size={14} className="mr-1" /> Ajouter</Button>;
}
function Row({ children, onDelete }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-2">
      {children}
      <button onClick={onDelete} className="text-dd-warm hover:text-destructive px-1"><Trash2 size={16} /></button>
    </div>
  );
}
function Empty() {
  return <p className="text-sm text-dd-warm">Rien détecté — ajoutez manuellement si nécessaire.</p>;
}
